// The route table (SPEC §4). URLs keep react-admin's shapes under
// APP_BASE_PATH (ADR 0017). Feature areas are placeholders until phase 5
// replaces each with its lazily loaded routes module (lazyRoute.ts).
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom'
import {
  AccessDenied,
  AuthCallbackPage,
  canAccess,
  GmailCallbackPage,
  LoginPage,
  OaseCallbackPage,
  RequireAccess,
  RequireAuth,
  useSession
} from '../auth'
import { isPortedArea, NotFound, PortalNotice, type CrumbHandle } from '../shell'
import { moduleVersionsRoutes } from '../features/moduleversions/routes'
import { modulesRoutes } from '../features/modules/routes'
import { portalUsersRoutes } from '../features/portalusers/routes'
import { profilesRoutes } from '../features/profiles/routes'
import { releasesRoutes } from '../features/releases/routes'
import { tenantsRoutes } from '../features/tenants/routes'
import { upgradeProcessRoutes } from '../features/upgradeprocess/routes'
import { wavesRoutes } from '../features/waves/routes'
import { settingsRoutes } from '../features/settings/routes'
import { AreaPlaceholder } from './AreaPlaceholder'
import { LANDING_ORDER, MENU, PORTAL_USERS, UPGRADE_PROCESS, type AreaLink } from './areas'
import { PageLoader } from '../ui'
import { RouteError } from './RouteError'
import { ShellLayout } from './ShellLayout'

const crumb = (label: string): CrumbHandle => ({ crumb: () => label })

/** `/` → the first area the user may list, in react-admin's registration order. */
function Landing() {
  const { permissions } = useSession()
  const first = LANDING_ORDER.find((link) =>
    canAccess({ permissions, resource: link.resource, action: 'list' })
  )
  return first ? <Navigate to={first.path} replace /> : <AccessDenied />
}

/** Areas ported so far (phase 5 adds one per commit), keyed by their path. */
const IMPLEMENTED: Record<string, RouteObject[]> = {
  '/tenants': tenantsRoutes,
  '/releases': releasesRoutes,
  '/waves': wavesRoutes,
  '/modules': modulesRoutes,
  '/moduleversions': moduleVersionsRoutes,
  // Both upgrade-process lists live in one area module.
  '/upgradeprocess/tenants': upgradeProcessRoutes,
  '/upgradeprocess/releases': [],
  '/portalusers': portalUsersRoutes
}

/** An area's routes: the portal notice when unported, its pages when built, else a placeholder. */
function areaRoutes(link: AreaLink): RouteObject[] {
  const path = link.path.replace(/^\//, '')
  if (!isPortedArea(link.area)) {
    return [{ path: `${path}/*`, element: <PortalNotice />, handle: crumb(link.label) }]
  }
  const built = IMPLEMENTED[link.path]
  if (built) return built
  return [
    {
      path: `${path}/*`,
      handle: crumb(link.label),
      element: (
        <RequireAccess resource={link.resource} action="list">
          <AreaPlaceholder label={link.label} />
        </RequireAccess>
      )
    }
  ]
}

export const routes: RouteObject[] = [
  // Public, outside the shell. The callback URLs are fixed: MSAL and the
  // OAuth backends redirect to them (ADR 0008).
  { path: 'login', element: <LoginPage /> },
  { path: 'auth-callback', element: <AuthCallbackPage /> },
  { path: 'auth/oase-callback', element: <OaseCallbackPage /> },
  { path: 'auth/gmail_callback', element: <GmailCallbackPage /> },
  ...(import.meta.env.MODE !== 'production'
    ? [
        {
          path: '__ui',
          lazy: async () => {
            const { UiPreview } = await import('./UiPreview')
            return { Component: UiPreview }
          }
        }
      ]
    : []),
  {
    path: '/',
    element: (
      <RequireAuth>
        <ShellLayout />
      </RequireAuth>
    ),
    errorElement: <RouteError />,
    // Shown while the first lazily loaded page of a deep link arrives.
    hydrateFallbackElement: <PageLoader />,
    children: [
      { index: true, element: <Landing /> },
      ...[...MENU, ...UPGRADE_PROCESS, PORTAL_USERS].flatMap(areaRoutes),
      // Profiles live under a tenant (/tenants/:id/profiles/...); see profiles/routes.tsx.
      ...(isPortedArea('tenants') ? profilesRoutes : []),
      ...settingsRoutes,
      { path: '*', element: <NotFound /> }
    ]
  }
]

export function createRouter() {
  return createBrowserRouter(routes, { basename: import.meta.env.APP_BASE_PATH })
}

export type AppRouter = ReturnType<typeof createRouter>
